import React, { useCallback, useEffect, useMemo } from 'react'
import { DatePicker, Form, Input, Select, Spin, TimePicker } from 'antd'
import { useStore, useUnit } from 'effector-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import dayjs, { Dayjs } from 'dayjs'
import { debounce } from 'lodash'

import { $params } from '../../../../../features/router'
import { $userProfile } from '../../../../../features/user'
import { showNotification } from '../../../../../features/notifications'
import { decimalValidator, parseDecimal } from '../../../../../libs/utils'
import {
  createChargeback,
  createChargebackMounted,
} from '../../../../billing/chargeback/create/model'
import { CHARGEBACK_STATUSES } from '../../../../billing/chargeback/list/consts'
import {
  $transactionOptions,
  getTransactions,
  transactionsSearched,
} from '../../../../billing/chargeback/transactionsModel'
import { ChargebackStatus } from '../../../../billing/chargeback/types'
import { $wallet as $currentWallet } from '../../details/model'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { TransactionModalLayout } from '../TransactionModalLayout'
import { ModalType } from '../types'

type Props = {
  isOpen: boolean
  onClose: () => void
  onCancel?: () => void
  type: ModalType
}

type FormValues = {
  walletId: string
  transactionId: string
  amount: string
  currency: string | null
  chbCommission: string
  commissionCurrency: string | null
  exchangeRate: string
  date: Dayjs | null
  time: Dayjs | null
  status: ChargebackStatus
  comment: string
}

const CHARGEBACK_TYPE_LABEL = 'Chargeback'

const rowStyle = { display: 'flex', gap: 8 }

const fieldStyle = { flex: 1, minWidth: 0 }

const currencyFieldStyle = { width: 160, flexShrink: 0 }

const decimalRule =
  (options: { allowEmpty?: boolean; allowNegative?: boolean } = {}) =>
    (value: string) =>
      decimalValidator(options)(null, value).then(
        () => true,
        (message) => String(message),
      )

const toIsoDateTime = (date: Dayjs, time: Dayjs) =>
  date
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second())
    .millisecond(0)
    .toISOString()

export const ChargebackTransactionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCancel,
  type,
}) => {
  const userProfile = useStore($userProfile)
  const wallets = useUnit($walletAllowedList)
  const currentWallet = useStore($currentWallet)
  const params = useStore($params)
  const transactionOptions = useUnit($transactionOptions)
  const isTransactionsLoading = useUnit(getTransactions.pending)
  const isSubmitting = useUnit(createChargeback.pending)

  const isWalletPage = type === 'wallet' || !!params?.walletID

  const initialWalletId =
    params?.walletID ||
    (type === 'wallet' && currentWallet.id ? String(currentWallet.id) : '')

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      walletId: initialWalletId,
      transactionId: '',
      amount: '',
      currency: null,
      chbCommission: '',
      commissionCurrency: null,
      exchangeRate: '',
      date: dayjs(),
      time: dayjs(),
      status: 'Pending',
      comment: '',
    },
  })

  const walletId = useWatch({ control, name: 'walletId' })
  const currency = useWatch({ control, name: 'currency' })
  const commissionCurrency = useWatch({ control, name: 'commissionCurrency' })

  const walletCurrencies = useMemo(() => {
    if (isWalletPage && currentWallet.id) {
      return currentWallet.localBalances?.map((item) => item.currency) ?? []
    }
    const wallet = wallets.find((item) => Number(item.id) === Number(walletId))
    return wallet?.currencies ?? []
  }, [wallets, walletId, currentWallet, isWalletPage])

  const currencyOptions = useMemo(
    () => walletCurrencies.map((item) => ({ value: item, label: item })),
    [walletCurrencies],
  )

  const debouncedTransactionsSearch = useCallback(
    debounce(transactionsSearched, 1000),
    [],
  )

  useEffect(() => {
    if (isOpen) {
      transactionsSearched('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const unsubscribeDone = createChargeback.done.watch(() => {
      showNotification({
        type: 'success',
        description: 'Chargeback created successfully',
      })
      onClose()
    })
    const unsubscribeFail = createChargeback.failData.watch((data) =>
      showNotification({
        type: 'error',
        message: 'Error',
        description: data.body.message,
        placement: 'topRight',
      }),
    )

    return () => {
      unsubscribeDone()
      unsubscribeFail()
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (currency && !commissionCurrency) {
      setValue('commissionCurrency', currency)
    }
  }, [currency, commissionCurrency, setValue])

  // Сумма и валюта чарджбэка по умолчанию берутся из выбранной транзакции;
  // валюту подставляем, только если она есть на балансе кошелька
  const applyTransactionDefaults = (transactionId: string) => {
    const transaction = transactionOptions.find(
      (option) => option.value === transactionId,
    )
    if (!transaction) return

    setValue('amount', transaction.clientAmount ?? '', {
      shouldValidate: true,
    })

    if (walletCurrencies.includes(transaction.clientCurrency)) {
      setValue('currency', transaction.clientCurrency, {
        shouldValidate: true,
      })
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    onClose()
  }

  const onSubmit = (values: FormValues) => {
    createChargebackMounted({
      wallet: Number(values.walletId),
      dateTime: toIsoDateTime(values.date!, values.time!),
      transactionId: Number(values.transactionId),
      amount: parseDecimal(values.amount),
      currency: values.currency as string,
      chbCommission: parseDecimal(values.chbCommission),
      commissionCurrency: (values.commissionCurrency ||
        values.currency) as string,
      exchangeRate: values.exchangeRate
        ? parseDecimal(values.exchangeRate)
        : null,
      status: values.status,
      comment: values.comment,
    })
  }

  return (
    <TransactionModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      title="Create chargeback"
      creatorEmail={userProfile.email}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Form.Item вне контекста Form рисуется горизонтально —
          component={false} даёт вертикальную раскладку без своего <form> */}
      <Form layout="vertical" requiredMark={false} component={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={rowStyle}>
            <Controller
              name="walletId"
              control={control}
              rules={{ required: 'Wallet is required' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Wallet"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <Select
                    {...field}
                    showSearch
                    optionFilterProp="label"
                    disabled={isWalletPage}
                    placeholder="Select wallet"
                    options={wallets.map((wallet) => ({
                      value: String(wallet.id),
                      label: wallet.name,
                    }))}
                  />
                </Form.Item>
              )}
            />
            <Form.Item label="Type" style={fieldStyle}>
              <Select
                value={CHARGEBACK_TYPE_LABEL}
                disabled
                options={[
                  {
                    value: CHARGEBACK_TYPE_LABEL,
                    label: CHARGEBACK_TYPE_LABEL,
                  },
                ]}
              />
            </Form.Item>
          </div>

          <div style={rowStyle}>
            <Controller
              name="transactionId"
              control={control}
              rules={{
                required: 'Transaction ID is required',
                validate: (value) =>
                  /^\d+$/.test(String(value ?? '').trim()) || 'Integer number',
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Transaction ID"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <Select
                    {...field}
                    showSearch
                    filterOption={false}
                    loading={isTransactionsLoading}
                    placeholder="E.g. 100234"
                    optionLabelProp="value"
                    popupMatchSelectWidth={false}
                    notFoundContent={
                      isTransactionsLoading ? <Spin size="small" /> : null
                    }
                    onSearch={debouncedTransactionsSearch}
                    onChange={(value) => {
                      field.onChange(value)
                      applyTransactionDefaults(value)
                    }}
                    options={transactionOptions}
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Form.Item label="Status" style={fieldStyle}>
                  <Select
                    {...field}
                    options={CHARGEBACK_STATUSES.map((status) => ({
                      value: status,
                      label: status,
                    }))}
                  />
                </Form.Item>
              )}
            />
          </div>

          <div style={rowStyle}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: 'Amount is required',
                validate: decimalRule({
                  allowEmpty: false,
                  allowNegative: true,
                }),
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Amount"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <Input {...field} placeholder="0" inputMode="decimal" />
                </Form.Item>
              )}
            />
            <Controller
              name="currency"
              control={control}
              rules={{ required: 'Currency is required' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label=" "
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={currencyFieldStyle}
                >
                  <Select
                    {...field}
                    showSearch
                    placeholder="Currency"
                    options={currencyOptions}
                  />
                </Form.Item>
              )}
            />
          </div>

          <div style={rowStyle}>
            <Controller
              name="chbCommission"
              control={control}
              rules={{ validate: decimalRule() }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="CHB commission"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <Input {...field} placeholder="0" inputMode="decimal" />
                </Form.Item>
              )}
            />
            <Controller
              name="commissionCurrency"
              control={control}
              render={({ field }) => (
                <Form.Item label=" " style={currencyFieldStyle}>
                  <Select
                    {...field}
                    showSearch
                    allowClear
                    placeholder="Currency"
                    options={currencyOptions}
                  />
                </Form.Item>
              )}
            />
          </div>

          <Controller
            name="exchangeRate"
            control={control}
            rules={{ validate: decimalRule() }}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                label="Exchange rate"
                tooltip="Курс конвертации суммы в USD на момент операции"
                validateStatus={error ? 'error' : undefined}
                help={error?.message}
              >
                <Input {...field} placeholder="0" inputMode="decimal" />
              </Form.Item>
            )}
          />

          <div style={rowStyle}>
            <Controller
              name="date"
              control={control}
              rules={{ required: 'Date is required' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Date"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <DatePicker
                    {...field}
                    style={{ width: '100%' }}
                    format="DD.MM.YYYY"
                  />
                </Form.Item>
              )}
            />
            <Controller
              name="time"
              control={control}
              rules={{ required: 'Time is required' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Time"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                  style={fieldStyle}
                >
                  <TimePicker
                    {...field}
                    style={{ width: '100%' }}
                    format="HH:mm:ss"
                  />
                </Form.Item>
              )}
            />
          </div>

          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <Form.Item label="Comment">
                <Input.TextArea {...field} rows={2} />
              </Form.Item>
            )}
          />
        </form>
      </Form>
    </TransactionModalLayout>
  )
}
